import { Cell } from '@table-library/react-table-library/table';

import { comparisonColumns, contributorColumns } from '../GscTools/columns';
import { inspectionLink, pageQueriesLink, queryPagesLink } from './rows';
import { CONTRIBUTORS } from './scope';
import { caseInsensitive, filterLabel, policyLabel, topicFilters, topicMatch } from './topics';

/**
 * The columns this report defines on top of the shared GSC tool sets.
 *
 * The metric columns are always the shared ones, so a topic row here and a query row anywhere
 * else in the app report the same numbers under the same headings; only the key columns, the
 * overlap column and the row actions are this report's own.
 */

const bold = ({ data, csvDownload }) => (csvDownload ? data : <strong>{data}</strong>);

/** Block 02 — each topic, this window against its baseline. */
export function comparisonTableColumns(t, { hasPosition }) {
    const shared = comparisonColumns(t, {
        hasPosition,
        keyLabel: t('tools.topic_clusters.comparison.topic'),
        isUrl: false,
    });

    return [
        {
            label: t('tools.topic_clusters.comparison.topic'),
            key: 'key',
            data: 'key',
            sortType: 'string',
            cellSize: 'minmax(180px, 1.6fr)',
            Component: Cell,
            renderCell: bold,
        },
        ...shared.slice(1),
    ];
}

/**
 * Block 01 — every applied topic: what it matches on, under which policy and case rule, and the
 * filters Search Console was sent. The last column is the one to read when a figure looks wrong:
 * the escaping, the anchors and the case flag are visible only there.
 */
export function definitionColumns(t) {
    const column = (key, size, renderCell) => ({
        label: t(`tools.topic_clusters.definition.${key}`),
        key,
        data: key,
        sortType: 'string',
        cellSize: size,
        Component: Cell,
        renderCell,
    });

    return [
        column('topic', 'minmax(150px, 1fr)', bold),
        column('policy', 'minmax(170px, 1.2fr)'),
        column('match', 'minmax(200px, 1.6fr)'),
        column('case', 'minmax(140px, 1fr)'),
        column('sent', 'minmax(280px, 2.4fr)', ({ data, csvDownload }) =>
            csvDownload ? data : <code className="gsc-tool-expression">{data}</code>,
        ),
    ];
}

export function definitionRows(t, scope) {
    return scope.topics.map((topic) => ({
        id: topic.name,
        topic: topic.name,
        policy: policyLabel(t, topic.policy),
        match: topicMatch(topic),
        case: caseInsensitive(topic)
            ? t('tools.topic_clusters.definition.case_insensitive')
            : t('tools.topic_clusters.definition.case_sensitive'),
        // Several filters are ANDed in one group; the column says so in the words the export uses.
        sent: topicFilters(topic).map(filterLabel).join(' AND '),
    }));
}

/**
 * The other topics a query also meets. A column rather than a footnote, because the export
 * outlives the page: a file of one topic's queries that hid which of them are also in another
 * topic would invite the two files to be added together.
 */
function alsoInColumn(t) {
    return {
        label: t('tools.topic_clusters.contributors.also_in'),
        key: 'alsoIn',
        data: 'alsoIn',
        sortType: (array) =>
            array.sort((a, b) => (a.alsoIn?.length ?? 0) - (b.alsoIn?.length ?? 0)),
        cellSize: 'minmax(150px, 1.2fr)',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => {
            if (data?.length) return data.join(', ');
            const none = t('tools.topic_clusters.contributors.only_this');
            return csvDownload ? none : <span className="text-muted">{none}</span>;
        },
    };
}

/** Block 04 — the shared metric columns keyed by query or page, plus the overlap for queries. */
export function contributorTableColumns(t, { hasPosition, view }) {
    const isPages = view === CONTRIBUTORS.PAGES;
    const shared = contributorColumns(t, {
        hasPosition,
        keyLabel: isPages ? t('tools.gsc.columns.page') : t('tools.gsc.columns.query'),
        isUrl: isPages,
    });

    return isPages ? shared : [...shared, alsoInColumn(t)];
}

/**
 * The row actions: the tools that answer the next question about one exact row. A query opens its
 * page contributors; a page opens the topic's queries on it, or is carried into URL Inspection
 * without being inspected. Each opens in its own tab so the topics on screen stay as they are, and
 * the column is left out of the exports — a link is not a value.
 */
export function contributorActionColumn(t, { scope, period, topic, view }) {
    const isPages = view === CONTRIBUTORS.PAGES;

    const link = (href, label, title) => (
        <a
            key={label}
            className="table-action-main-btn"
            href={href}
            target="_blank"
            rel="noreferrer"
            title={title}
        >
            {label}
        </a>
    );

    return {
        label: t('tools.topic_clusters.contributors.open_in'),
        key: 'actions',
        data: 'key',
        sortType: (array) => array,
        cellSize: isPages ? 'minmax(200px, 220px)' : 'minmax(120px, 140px)',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return '';

            return (
                <div className="gsc-tool-row-links">
                    {isPages
                        ? [
                              link(
                                  pageQueriesLink(scope, item.key, period, topic),
                                  t('tools.topic_clusters.contributors.queries'),
                                  t('tools.topic_clusters.contributors.queries_title'),
                              ),
                              link(
                                  inspectionLink(scope, item.key),
                                  t('tools.topic_clusters.contributors.inspect'),
                                  t('tools.topic_clusters.contributors.inspect_title'),
                              ),
                          ]
                        : link(
                              queryPagesLink(scope, item.key, period),
                              t('tools.topic_clusters.contributors.pages'),
                              t('tools.topic_clusters.contributors.pages_title'),
                          )}
                </div>
            );
        },
    };
}
